import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const SPLIT_IMAGE =
  "https://images.pexels.com/photos/7078392/pexels-photo-7078392.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

/**
 * Split-page chrome shared by /login, /signup and /invite/:token.
 * Asymmetric layout: 5/12 form on the left, 7/12 imagery + product story on the right.
 */
export function AuthShell({ children }) {
  const { t } = useTranslation(["common", "auth"]);
  return (
    <div className="min-h-screen w-full bg-white text-slate-900">
      <div className="flex min-h-screen flex-col md:flex-row">
        {/* Left: form column */}
        <div className="relative flex w-full flex-col md:w-5/12">
          <div className="flex items-center justify-between px-8 py-6 lg:px-12">
            <Link to="/login" data-testid="auth-brand" className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 text-white">
                <span className="font-display text-sm font-semibold tracking-tighter">E</span>
              </div>
              <span className="font-display text-base font-semibold tracking-tight">
                EvidenceLab
              </span>
            </Link>
            <LanguageSwitcher />
          </div>
          <div className="flex flex-1 items-center justify-center px-8 pb-12 lg:px-16">
            <div className="w-full max-w-md">{children}</div>
          </div>
          <div className="px-8 pb-8 text-xs text-slate-400 lg:px-12">
            <span className="font-display tracking-wide">© EvidenceLab · Phase 1</span>
          </div>
        </div>

        {/* Right: imagery + tagline */}
        <div className="relative hidden overflow-hidden bg-slate-900 md:flex md:w-7/12">
          <img
            src={SPLIT_IMAGE}
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/80 via-slate-900/60 to-indigo-900/60" />
          <div className="bg-grid-slate absolute inset-0 opacity-20" />
          <div className="relative flex h-full w-full flex-col justify-between p-12 text-white">
            <div className="max-w-md">
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-indigo-200/80">
                {t("common:appName")}
              </span>
              <h2 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight lg:text-5xl">
                {t("auth:split.headline")}
              </h2>
              <p className="mt-4 max-w-sm text-base text-slate-200/80">
                {t("auth:split.subline")}
              </p>
            </div>
            <div className="grid max-w-md grid-cols-3 gap-6 border-t border-white/10 pt-8">
              <div>
                <div className="font-display text-2xl font-semibold">12K+</div>
                <div className="text-[11px] uppercase tracking-wider text-slate-300/80">
                  {t("auth:split.stat1")}
                </div>
              </div>
              <div>
                <div className="font-display text-2xl font-semibold">0.87</div>
                <div className="text-[11px] uppercase tracking-wider text-slate-300/80">
                  {t("auth:split.stat2")}
                </div>
              </div>
              <div>
                <div className="font-display text-2xl font-semibold">3</div>
                <div className="text-[11px] uppercase tracking-wider text-slate-300/80">
                  {t("auth:split.stat3")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
